import qs from 'query-string'
import { isEmpty } from './is'
import type { LocationQuery, Router } from 'vue-router'

export const SEARCH_PREFIX = '_s_'
export const PAGE_PREFIX = '_p_'
export const PAGE_PROPS = ['pageSize', 'pageNum']
export const PREFIX_REG = new RegExp(`^${PAGE_PREFIX}|^${SEARCH_PREFIX}`)
export const SEARCH_PREFIX_REG = new RegExp(`^${SEARCH_PREFIX}`)

/**
 * 从URL中获取查询参数对象。
 * @param url - 可选参数。指定的URL字符串，如果不提供，则使用当前页面的URL。
 * @returns 返回一个对象，对象的键值对表示URL中的查询参数。
 * @example
 * getQueryObject('http://www.example.com/index.html?a=1&b=2')
 * // 返回 {a: '1', b: '2'}
 * @source
 */
export function getQueryObject(url?: string): any {
  // 如果没有提供URL，则使用当前页面的URL
  url = url == null ? window.location.href : url
  // 获取URL中查询参数部分的字符串
  const search = url.slice(url.lastIndexOf('?') + 1)
  const obj = {}
  const reg = /([^?&=]+)=([^?&=]*)/g
  // 使用正则表达式匹配并解析查询参数
  search.replace(reg, (rs, $1, $2) => {
    const name = decodeURIComponent($1)
    let val = decodeURIComponent($2)
    val = String(val)
    // 对于重复的参数键，取最后一个出现的值
    // 特殊case：pageId=80923640&token=123&token=345, 取最后的token
    obj[name] = val
    return rs
  })
  return obj
}

/**
 * 为URL追加查询参数。
 *
 * @param baseUrl 基础URL，将查询参数追加到此URL上。
 * @param obj 要转换为查询参数的对象。对象的每个属性和值都会被编码并添加为URL的查询参数。
 * @example
 * setObjToUrlParams('http://www.example.com/index.html', {a: 1, b: '2'})
 * // 返回 'http://www.example.com/index.html?a=1&b=2'
 * @returns 返回带有查询参数的完整URL字符串。
 *
 * @source
 */
export function setObjToUrlParams(baseUrl: string, obj: any): string {
  // 初始化参数字符串
  let parameters = ''

  // 遍历对象，将每个属性和值编码并添加到参数字符串
  for (const key in obj) {
    parameters += `${key}=${encodeURIComponent(obj[key])}&`
  }

  // 移除参数字符串末尾的多余的&
  parameters = parameters.replace(/&$/, '')

  if (!parameters) return baseUrl
  if (baseUrl.endsWith('?') || baseUrl.endsWith('&')) {
    return `${baseUrl}${parameters}` // If the baseUrl ends with ? or &, simply append the parameters
  } else if (baseUrl.includes('?')) {
    return `${baseUrl}&${parameters}` // If the baseUrl already contains ?, append the parameters with &
  } else {
    return `${baseUrl}?${parameters}` // If the baseUrl doesn't contain ?, append the parameters with ?
  }
}

/**
 * 将参数格式化为查询字符串。
 *
 * 此函数用于处理和格式化参数，以便将它们集成到查询字符串中。
 * 它首先从当前URL中提取现有的查询参数，然后根据特定规则处理和添加新的参数。
 * 规则包括为特定属性添加前缀，以及根据参数的类型和值来格式化参数。
 *
 * @param params 待格式化的参数对象。
 * @returns 返回格式化后的查询参数对象。
 *
 * @source
 */
export const formatParamsToQuery = (params): Record<string, any> => {
  // 从当前URL中获取现有的查询参数对象
  const sourceQuery = getQueryObject(location.href)
  // 删除具有特定前缀的属性，这些属性不需要包含在最终的查询字符串中
  // 把老的query带前缀的给删了
  const targetQuery = deletePropsByPrefix(sourceQuery)

  // 遍历传入的参数对象
  // 给table的属性都加上下划线开头，区分出非下划线的变量
  for (const key in params) {
    let curParam = params[key]
    // 检查当前参数是否为指定的页面属性，如果不是，则添加前缀并继续
    if (PAGE_PROPS.includes(key)) {
      targetQuery[PAGE_PREFIX + key] = curParam
      continue
    }
    // 获取当前参数的类型
    // 将值的类型保存到query中，因为url会把数字强转成字符串所以需要将类型保留上去
    let paramType: string = typeof curParam
    // 如果参数是对象类型，进一步处理
    if (paramType === 'object') {
      // 如果参数是数组且数组不为空，特殊处理数组类型
      if (Array.isArray(curParam) && curParam.length > 0) {
        paramType = `${typeof curParam[0]}Array`
        // 如果数组中的元素是对象，将数组转换为字符串
        // 二维数组特殊处理
        if (paramType === 'objectArray') {
          curParam = JSON.stringify(curParam)
        }
      }
    }
    // 根据参数的类型和值，格式化参数，并添加到目标查询参数对象中
    targetQuery[SEARCH_PREFIX + key] = !isEmpty(curParam) ? `${paramType}[${curParam}]` : curParam
  }

  return targetQuery
}

/**
 * 删除当前URL中键名以_p_、_s_开头的参数。
 * *
 * @example
 * // 假设当前页面URL为 'http://www.example.com/index.html?a=1&_p_pageNum=1&_s_name=nice'
 * deleteQueryByPrefix()
 * // 当前页面URL变为 'http://www.example.com/index.html?a=1'
 * @source
 */
export const deleteQueryByPrefix = (): void => {
  // 从当前页面URL中获取查询参数对象
  const sourceQuery = getQueryObject(location.href)
  // 删除查询参数中所有以特定前缀开头的项
  const targetQuery = deletePropsByPrefix(sourceQuery)
  // 根据更新后的查询参数对象格式化URL
  const url = formatUrlByQuery(targetQuery)
  // 替换当前页面URL的查询参数部分为新格式化的URL
  replaceUrlQuery(url)
}

/**
 * 替换URL查询参数。
 *
 * 该函数用于更新浏览器的历史记录状态，以替换当前URL中的查询参数。这在不刷新页面的情况下改变URL很有用，例如在单页面应用中导航时。
 *
 * @param url 要替换的完整URL字符串，包括查询参数。如果URL中包含 '#' 符号，它将被去除。
 *
 * @source
 */
export const replaceUrlQuery = (url: string): void => {
  // 使用history.replaceState替换当前的历史记录状态，其中current属性被更新为去除'#'符号的url。
  // 这样做可以改变浏览器地址栏的显示，而不会触发页面刷新或回退。
  // 第一个是更新history.state.current更新history的当前页面堆栈，vue的跳转都是依靠history.state
  history.replaceState({ ...history.state, current: url.replace('#', '') }, '', url)
}

/**
 * 根据搜索参数替换当前URL的查询字符串。
 *
 * 本函数的目的是通过一组新的搜索参数来动态更新当前页面的URL，保持页面状态的一致性和可书签性。
 * 它首先根据传入的参数格式化URL，然后使用格式化的URL替换当前URL的查询字符串部分。
 * 这样做可以在不刷新页面的情况下，通过改变URL来反映页面状态，方便用户通过收藏或分享URL来保存和分享当前页面状态。
 *
 * @param params 一个包含搜索参数的对象。这些参数将被用于生成新的URL查询字符串。
 *
 * @source
 */
export const replaceUrlBySearchParams = (params: Record<string, any>): void => {
  // 根据传入的参数格式化URL。
  const url = formatUrlByParams(params)
  // 使用格式化的URL替换当前URL的查询字符串。
  replaceUrlQuery(url)
}

/**
 * 根据指定的前缀删除对象中的属性。
 * 此函数接受一个对象作为参数，并返回一个新对象，该新对象不包含任何以指定前缀开头的属性。
 * @param query 可选参数，代表需要处理的对象。如果未提供，则函数将返回一个空对象。
 * @returns 返回一个新的对象，该对象不包含任何以指定前缀开头的属性。
 *
 * @source
 */
function deletePropsByPrefix(query?: Record<string, any>): Record<string, any> {
  // 初始化一个空对象，用于存储不带有指定前缀的属性
  const targetQuery = {}
  // 遍历输入对象的所有属性
  // 清除老的table的属性，带两种前缀的属性
  for (const key in query) {
    // 使用正则表达式检查属性名是否包含指定前缀
    if (PREFIX_REG.test(key)) {
      // 如果属性名包含指定前缀，则跳过当前循环，不将该属性复制到新对象中
      continue
    }
    // 如果属性名不包含指定前缀，则将该属性及其值复制到新对象中
    targetQuery[key] = query[key]
  }
  // 返回新对象，该对象不包含任何以指定前缀开头的属性
  return targetQuery
}

/**
 * 过滤对象中的空属性。
 * 空属性指的是值为undefined或null的属性。
 * 此函数直接修改传入的对象，移除空属性。
 *
 * @param obj 要过滤的对象
 *
 * @source
 */
const filterVoidProps = (obj): void => {
  // 遍历对象的所有键
  Object.keys(obj).forEach((item) => {
    // 如果当前属性的值为undefined或null，则删除该属性
    if (obj[item] === undefined || obj[item] === null) {
      delete obj[item]
    }
  })
}

/**
 * 根据参数对象格式化URL。
 *
 * 该函数接收一个参数对象，将其转换为查询字符串并附加到当前页面URL的哈希部分之前。
 * 主要用于在单页面应用中根据参数动态生成或更新URL，以便于导航和书签功能。
 *
 * @example
 * // 假设当前页面URL为 'http://www.example.com/index.html?a=1'
 * // params  {name: 'John',age: 30,hobby: ['reading', 'music'],location: {city: 'New York',country: 'USA'}};
 * formatUrlByParams()
 * // 当前页面URL变为 'http://www.example.com/index.html?_p_age=30&_p_hobby=reading&_p_hobby=music&_p_location=%5Bobject%20Object%5D&_p_name=John&a=1'
 *
 * @param params 参数对象，包含需要添加到URL的查询参数。
 * @returns 返回格式化后的URL字符串。
 *
 * @source
 */
export const formatUrlByParams = (params): string => {
  // 移除参数对象中的空属性，避免生成无效的查询参数
  filterVoidProps(params)
  // 将参数对象转换为查询字符串格式
  const queryObj = formatParamsToQuery(params)
  // 构造最终的URL字符串，包括当前页面的哈希部分和格式化的查询字符串
  return `${location.hash.split('?')[0]}?${qs.stringify(queryObj)}`
}

/**
 * 根据查询参数格式化URL。
 *
 * 该函数用于将给定的查询参数对象转换为URL查询字符串，并将其附加到当前页面URL的哈希部分之前。
 * 这样可以在不刷新页面的情况下，通过改变URL的查询参数来动态更新页面状态或内容。
 *
 * @example
 * // 假设当前页面URL为 http://www.example.com/index.html#/home?a=1
 * formatUrlByQuery
 * // 当前页面URL变为 http://www.example.com/index.html#/home?address=%5Bobject%20Object%5D&age=25&hobbies=reading&hobbies=music&name=Alice
 *
 * @param query 查询参数对象，其中的键值对将被转换为URL查询字符串的一部分。
 * @returns 返回格式化后的URL字符串，包括原有的哈希部分和新增的查询参数。
 *
 * @source
 */
export const formatUrlByQuery = (query): string => {
  // 从当前页面URL的哈希部分提取出路径，并拼接上查询参数字符串
  return `${location.hash.split('?')[0]}?${qs.stringify(query)}`
}

/**
 * 将查询参数解析为筛选条件。
 *
 * 该函数用于处理路由查询参数，提取其中用于表格筛选的参数，并决定是否显示筛选面板。
 * 它通过识别参数键名中是否包含特定前缀来识别筛选参数。
 * 配合 BasicTable 使用
 *
 * @param query 查询参数对象，通常来自路由的查询字符串。
 * @returns 返回一个对象，包含解析后的筛选参数和一个标志位，指示是否应该显示筛选面板。
 *
 * @example
 * const { isFilterShow, filterParams } = parseQueryToFilter(qs.parse(location.search))
 *
 * @source
 */
export const parseQueryToFilter = (
  query: LocationQuery,
): { filterParams: Record<string, any>; isFilterShow: boolean } => {
  // 初始化用于存储筛选参数的对象
  // table的筛选form的筛选项目初始值
  const filterParams = {}
  // 初始化标志位，用于指示是否应该显示筛选面板
  // table是否默认打开筛选form初始值
  let isFilterShow = false

  // 遍历查询参数对象的所有键
  for (const key in query) {
    // 使用正则表达式检查键名是否以指定前缀开头
    // 只取出带有筛选前缀_s_的字段
    if (SEARCH_PREFIX_REG.test(key)) {
      // 如果是筛选参数，处理并添加到筛选参数对象中
      filterPrefixContent(query, key, filterParams)
      // 设置标志位，表示存在筛选参数，因此应该显示筛选面板
      // 如果有筛选项，默认打开table的筛选form
      isFilterShow = true
    }
  }
  // 返回包含筛选参数和显示状态的对象
  return { filterParams, isFilterShow }
}

/**
 * 根据查询参数的键和值，过滤和转换查询条件。
 * @param query 查询参数对象。
 * @param key 查询参数的键，可能包含类型前缀。
 * @param filterParams 过滤后的参数对象，函数将结果填充到这个对象中。
 * @returns 无返回值，直接修改filterParams对象。
 *
 * @source
 */
export const filterPrefixContent = (query, key, filterParams): void => {
  // 正则表达式用于匹配参数键中的类型和内容部分。
  // 判断是什么类型的比如n number[0],类型就是number内容就是0
  const paramsRegex = /(.*?)\[(.*)\]/
  // 使用正则表达式匹配查询参数的键值对。
  const matchResult = query[key].match(paramsRegex)
  // 如果匹配结果是一个数组，并且包含3个元素，说明键值对中包含类型信息。
  if (Array.isArray(matchResult) && matchResult.length === 3) {
    // 提取参数类型和内容。
    // 解析出类型和内容
    const paramType = matchResult[1]
    let paramContent = matchResult[2]
    // 移除参数键中的类型前缀，得到真正的参数键名。
    const queryKey = key.replace(SEARCH_PREFIX_REG, '')
    // 根据参数类型，对内容进行转换，并将结果存入filterParams对象中。
    // 将内容还原成解析出的类型
    switch (paramType) {
      case 'number': {
        filterParams[queryKey] = Number(paramContent)
        break
      }
      case 'string': {
        filterParams[queryKey] = paramContent
        break
      }
      case 'numberArray': {
        // 将内容按逗号分隔，转换为数字数组。
        paramContent = paramContent.split(',')
        paramContent = paramContent.map((item) => Number(item))
        filterParams[queryKey] = paramContent
        break
      }
      case 'stringArray': {
        // 将内容按逗号分隔，转换为字符串数组。
        paramContent = paramContent.split(',')
        filterParams[queryKey] = paramContent
        break
      }
      case 'objectArray': {
        // 对象数组需要进行安全性检查，然后使用eval函数转换为JavaScript对象。
        // 安全校验
        if (/[[|{[\w-\\",:?]+\]|\},?]+/.test(paramContent)) {
          filterParams[queryKey] = eval(paramContent)
        }
        break
      }
    }
  } else {
    // 如果键值对中不包含类型信息，直接将值存入filterParams对象中。
    // 兼容老的类型
    filterParams[key.replace(SEARCH_PREFIX_REG, '')] = query[key]
  }
}

/**
 * 根据当前的浏览历史状态，决定返回上一页还是跳转到指定页面。
 * 函数会检查浏览器历史记录的状态，如果存在back属性，则会遍历目标URL数组，尝试从中匹配当前历史记录的back属性。如果匹配成功，则跳转到对应的目标URL；如果没有匹配成功，则会跳转到默认的目标URL。。
 *
 * @example
 * // 若当前页面历史记录为{ back: ['/page1', '/page5'] }，targetLastUrls为['/page1', '/page5']
 * backToSpecificPage(router, '/defaultUrl', ['/page1', '/page5'])
 * // 则跳转到/page1
 *
 * @param router 导航对象，用于执行返回或跳转操作。
 * @param defaultJumpUrl 默认跳转的URL，当没有匹配的历史记录时使用。
 * @param targetLastUrls 指定的历史记录URL列表，用于检查是否存在于历史记录中。
 *
 * @source
 */
export const backToSpecificPage = (
  router: Router,
  defaultJumpUrl: string,
  targetLastUrls: string[],
): void => {
  // 默认情况下，将跳转到默认URL
  let jumpToDefaultUrl = true
  // 检查浏览器历史记录的状态，如果存在back属性，则尝试从目标历史记录中匹配
  if (history?.state?.back) {
    // 遍历目标历史记录列表
    for (const item of targetLastUrls) {
      // 如果当前历史记录的back属性不包含目标URL，则返回上一页
      // 命中上一个历史记录路由，直接router.back跳转
      if (history.state.back.includes(item)) {
        router.back()
        // 设置不跳转到默认URL
        jumpToDefaultUrl = false
        break
      }
    }
  }
  // 如果没有返回上一页，则跳转到默认URL
  if (jumpToDefaultUrl) {
    // 构造跳转的路径，包括默认URL和当前页面的tab参数
    router.push({
      path: defaultJumpUrl,
      query: {
        tab: getQueryObject(location.href).tab,
      },
    })
  }
}

/**
 * 从URL中获取查询参数，并将空格编码为%20。
 *
 * @param url - 可选参数。指定的URL字符串，如果不提供，则使用当前窗口的URL。
 * @returns 返回一个对象，其中键是查询参数名，值是参数值。
 *
 * @source
 */
export function getQueryWithSpace(url?: string): Record<string, any> {
  // 如果没有提供URL，则使用当前窗口的URL。
  url = url == null ? window.location.href : url
  // 获取URL中的查询字符串部分。
  const search = url.slice(url.lastIndexOf('?') + 1)
  const obj = {}
  const reg = /([^?&=]+)=([^?&=]*)/g
  // 使用正则表达式匹配并解析查询字符串中的参数。
  search.replace(reg, (rs, $1, $2) => {
    // 解码参数名。
    const name = decodeURIComponent($1)
    let val = ''
    // 如果参数名符合特定条件，则对参数值进行额外的空格编码处理。
    // 只替换表格的filter搜索的query参数，不影响其它参数
    if (SEARCH_PREFIX_REG.test(name)) {
      /**
         W3C标准规定，当Content-Type为application/x-www-form-urlencoded时，URL中查询参数名和参数值中空格要用加号+替代，
         所以几乎所有使用该规范的浏览器在表单提交后，URL查询参数中空格都会被编成加号+。而在另一份规范(RFC 2396，定义URI)里, 
         URI里的保留字符都需转义成%HH格式(Section 3.4 Query Component)，因此空格会被编码成%20，加号+本身也作为保留字而被编成%2B，
         对于某些遵循RFC 2396标准的应用来说，它可能不接受查询字符串中出现加号+，认为它是非法字符。
         所以一个安全的举措是URL中统一使用%20来编码空格字符。
         Java中的URLEncoder本意是用来把字符串编码成application/x-www-form-urlencoded MIME格式字符串，也就是说仅仅适用于URL中的查询字符串部分
         ，但是URLEncoder经常被用来对URL的其他部分编码，它的encode方法会把空格编成加号+，与之对应的是，URLDecoder的decode方法会把加号+和%20都解码为空格, 
         这种违反直觉的做法造成了当初我对空格URL编码问题的困扰。因此后来我的做法都是，在调用URLEncoder.encode对URL进行编码后(所有加号+已被编码成%2B)，
         再调用replaceAll(“\+”, “%20″)，将所有加号+替换为%20。
         */
      val = decodeURIComponent($2.replaceAll('+', '%20'))
    } else {
      // 对于其他参数，直接解码参数值。
      // 获取其他的query参数
      val = decodeURIComponent($2)
    }
    // 确保参数值为字符串类型。
    val = String(val)
    // 将解码后的参数值存储到对象中。
    // 特殊case：pageId=80923640&token=123&token=345, 取最后的token
    obj[name] = val
    return rs
  })
  return obj
}

/**
 * 将参数添加到URL中。
 *
 * 该函数用于将一组参数添加到当前URL的查询字符串中。如果URL中已经存在相同的参数键，
 * 则新值会替换旧值。函数首先从当前URL中提取查询字符串，然后合并入新的参数，
 * 最后将更新后的查询字符串应用于URL。
 *
 * @example
 * // 假设当前页面URL为http://example.com/a.html?a=1 即将添加的参数params为{ newParam: 'newValue' }
 * addParamsToUrl(params)
 * // 最终得到的URL为http://example.com/a.html?a=1&newParam=newValue
 *
 * @param params 待添加到URL的参数对象。
 *
 * @source
 */
export function addParamsToUrl(params): void {
  // 从当前URL中提取查询字符串，并保留空格
  const query = getQueryWithSpace(location.href)
  // 合并当前查询参数和新参数
  const targetQuery = { ...query, ...params }
  // 构造新的URL，包括hash前的部分和编码后的查询字符串
  // hash 模式
  const url = `${location.hash.split('?')[0]}?${qs.stringify(targetQuery)}`
  // 替换浏览器地址栏的URL
  replaceUrlQuery(url)
}
